import { cardScanService, GetCardScanResultRequestV2, GetCardScanResultResponse } from '@afterpaytouch/portal-api/consumer/cardScan'
import { AsyncAction } from '../../types'
import { CardScanError, CheckoutError, DyScanFailureReason, isScanIntentV2, ScanIntentV2, ScanStatus } from '../../../model'
import { app } from '../.././../env'
import { checkpointSelector, setCardScanStatus, tokenSelector, traceIdSelector } from '../../'

const { getCardScanResultV2 } = cardScanService.endpoints

declare global {
  interface Window {
    DyScan: {
      isAvailable: () => Promise<boolean>
      Scan: new ({ key, config }) => ScanIntentV2
    }
  }
}

let scanIntentV2: ScanIntentV2 | null = null

export function getScanInstance(): ScanIntentV2 | null {
  if (scanIntentV2 == null) {
    const config = {
      showDynetiLogo: false,
      showExplanation: false,
      showResult: false,
      toastTimeout: 10000,
    }
    scanIntentV2 = new window.DyScan.Scan({ key: app.DYSCAN_API_KEY, config })
  }
  return scanIntentV2
}

export const initialiseScanIntent = (): AsyncAction<true | CheckoutError> => async (dispatch, getState) => {
  dispatch(setCardScanStatus(ScanStatus.Creating))

  const available = await window.DyScan.isAvailable()

  try {
    if (available) {
      dispatch(setCardScanStatus(ScanStatus.Ready))
      return true
    } else {
      dispatch(setCardScanStatus(ScanStatus.Initial))
      return {
        error: CardScanError.Unsupported,
      }
    }
  } catch (e) {
    dispatch(setCardScanStatus(ScanStatus.Initial))
    throw new Error()
  }
}

export const startCardScan = (): AsyncAction<undefined | true | CheckoutError> => async (dispatch, getState) => {
  const store = getState()
  const traceId: string = traceIdSelector(store)
  const token: string = tokenSelector(store)
  const checkpoint: string = checkpointSelector(store)

  try {
    dispatch(setCardScanStatus(ScanStatus.Scanning))

    const scan = getScanInstance()
    if (!isScanIntentV2(scan)) {
      return
    }
    // Afterpay doesn't need to pass any user id to DyScan based on the contract.
    // TODO: DyScan will have a fix to allow calling scan.present() with empty arguments.
    //  Will remove the placeholder for user id after DyScan fixes the error on their side.
    const scanResult = await scan.present('some-user-id', {})
    const { data, completed } = scanResult

    // Call the portal api /check/v2 if the scan is completed or is the scan is failed.
    // No need to call the portal api if the scan is canceled by the consumer.
    if (completed || (data !== null && data.status === 'scanFailed')) {
      const parameters: GetCardScanResultRequestV2 = {
        traceId,
        checkpoint,
        orderToken: token,
        scanId: data.scanId,
      }
      const getCardScanResultResponse: GetCardScanResultResponse = await dispatch(getCardScanResultV2.initiate(parameters, { forceRefetch: true })).unwrap()
      const { isMatched, isRetryable } = getCardScanResultResponse

      if (completed) {
        dispatch(setCardScanStatus(ScanStatus.Finished))

        if (isMatched) {
          return true
        }

        if (isRetryable) {
          return {
            error: CardScanError.DidntMatch,
          }
        }

        return {
          error: CardScanError.TooManyTries,
        }
      }
    }

    dispatch(setCardScanStatus(ScanStatus.Ready))
    if (!completed && data !== null) {
      switch (data.status) {
        case 'scanCancelled':
          return {
            error: CardScanError.UserCanceled,
          }
        case 'scanFailed':
          if (data.failureReason === DyScanFailureReason.NoWebcam) {
            dispatch(setCardScanStatus(ScanStatus.Initial))
            return {
              error: CardScanError.NoPermission,
            }
          }
          break
        default:
          break
      }
    }

    return {
      error: CardScanError.CannotScan,
    }
  } catch (e) {
    dispatch(setCardScanStatus(ScanStatus.Initial))
    return {
      error: CardScanError.TerminalError,
    }
  }
}
